<template>
  <div class="d-flex flex-column">
    <DxDataGrid v-bind="gridProperties" :data-source="gridSource" ref="grid" class="dx-card wide-card grid-operaciones">

      <DxToolbar>
        <DxItem location="after" template="exportButtonTemplate" />
        <DxItem v-bind="getResetFiltersGridBtn(() => grid)" />
        <DxItem v-bind="getResetStateGridBtn(() => grid)" />
        <DxItem v-bind="getColumnChooserBtn()" />
      </DxToolbar>

      <DxSelection mode="single" />
      <DxColumn data-field="CodigoSecuencial" :caption="$t('operaciones.grid.id')" format="0000000000" :show-in-column-chooser="false"></DxColumn>
      <DxColumn data-field="FechaAlta" :caption="$t('operaciones.grid.fecha_de_venta')" data-type="date" format="dd/MM/yyyy"></DxColumn>
      <DxColumn data-field="NombreComercial" :caption="$t('operaciones.grid.comercial')" />
      <DxColumn data-field="NombreEmpresaAgente" :caption="$t('operaciones.grid.empresa_agente')"></DxColumn>
      <DxColumn data-field="NombreAgente" :caption="$t('operaciones.grid.agente')"></DxColumn>
      <DxColumn data-field="NombreResponsable" :caption="$t('operaciones.grid.responsable')" />
      <DxColumn data-field="Comercializadora" :caption="$t('operaciones.grid.comercializadora')"></DxColumn>
      <DxColumn data-field="DocumentoTitular" :caption="$t('operaciones.grid.dni')"></DxColumn>
      <DxColumn data-field="CodigoEstado" :caption="$t('operaciones.grid.estado')" cell-template="estadoTemplate">
        <DxLookup v-bind="getLookup('operaciones', 'estado')"></DxLookup>
      </DxColumn>
      <DxColumn data-field="FechaModificacion" :caption="$t('operaciones.grid.fecha_ultima_modificacion')" data-type="date" format="dd/MM/yyyy"></DxColumn>
      <DxColumn data-field="NombreUsuarioUltimaModificacion" :caption="$t('operaciones.grid.nombre_usuario_ultima_modificacion')" />
      
      <!-- <DxColumn cell-template="irAChatTemplate" :fixed="true" fixed-position="right"></DxColumn> -->
      <DxColumn type="buttons" :caption="$t('operaciones.grid.acciones')" :fixed="true" fixed-position="right" :show-in-column-chooser="false" :buttons="[
        {
          cssClass: 'btn btn-link text-decoration-none btn-sm',
          text: $t('operaciones.grid.ir_a_chat'),
          icon: 'fa-regular fa-comment-dots text-success',
          onClick: (e: any) => irAChat(e)
        },
        {
          cssClass: 'btn btn-link text-decoration-none btn-sm',
          text: $t('operaciones.grid.ver_detalle'),
          icon: 'fa-solid fa-magnifying-glass text-success',
          onClick: (e: any) => verDetalle(e)
        }
      ]" />
      <DxColumnFixing :enabled="true" />

      <template #estadoTemplate="{ data }">
        <div :class="[
          `d-inline-block rounded py-1 px-2 `,
          `estado-operacion ${data.value.replace(' ', '')}`,
        ]">
            {{ $t(`operaciones.estado.${data.value}`) }}
        </div>
      </template>
      <template #exportButtonTemplate>
        <button @click="onExporting" class="btn btn-sm btn-light d-flex align-items-center gap-2 m-2">
          <i class="fas fa-file-excel exportar-excel"></i>
          <span class="text-primary">{{ $t("shared.ui.buttons.exportToExcel") }}</span>
        </button>
      </template>

      <DxMasterDetail :enabled="true" template="masterDetailTemplate" />
      <template #masterDetailTemplate="{ data: operacion }">
        <operaciones-agua-grid-detail :template-data="operacion" />
      </template>
    </DxDataGrid>
  </div>
</template>

<script setup lang="ts">
import { useApi } from "@api/integration";
import {
  DxColumn,
  DxDataGrid,
  DxLookup,
  DxMasterDetail,
  DxColumnFixing,
  DxSelection,
  DxToolbar,
  DxItem,
} from "devextreme-vue/data-grid";
import {
  getDefaultGridProperties,
  getLookup,
  exportGridToExcel,
  getDefaultGridWithStateStoringProperties,
  getColumnChooserBtn,
  getResetFiltersGridBtn,
  getResetStateGridBtn,
} from "@helpers/grid-helper";
import { useModalService } from "@services/modal-service";
import type CustomStore from "devextreme/data/custom_store";
import { onMounted, ref } from "vue";
import OperacionesAguaGridDetail from "@/components/operaciones-agua/OperacionesAguaGridDetail.vue";
import type DevExpress from "devextreme";
import { useIntl } from "@/.shared/services/i18n/i18nservice";
import { useRefreshGridOnVisible } from "@/.shared/composables/useRefreshGridOnVisible";
import { useRouter } from "@/.shared/services/routing-services";
import { TipoOperacionEnum } from "@/.shared/enums/TipoOperacionEnum";

const api = useApi();
const gridSource = ref<CustomStore>();
const grid = ref<DxDataGrid>();
const router = useRouter();
useRefreshGridOnVisible(grid);

const gridProperties = getDefaultGridWithStateStoringProperties("operaciones-agua-grid-state.v3", () => grid.value);

onMounted(async () => {
  gridSource.value = api.apiStore({
    key: "Id",
    load: await api.toStandardApiFunction("getOperacionesAguaForGrid", { showLoading: false })
  });
});

async function verDetalle(data: DevExpress.ui.dxDataGrid.ColumnButtonClickEvent) {
  await router.push({ name: "tramite", query: { idOperacion: data.row?.data.Id, tipoOperacion: TipoOperacionEnum.Agua } });
}

async function irAChat(data: DevExpress.ui.dxDataGrid.ColumnButtonClickEvent) {
  const rutaBO = import.meta.env.VITE_BASE_URL;
  const url = `${rutaBO}/comunicaciones-operacion/${data.row?.data.Id}/${data.row?.data.CodigoSecuencial.toString().padStart(10, '0')}/${TipoOperacionEnum.Agua}`;
  window.open(url, '_blank');
}

const onExporting = () => {
  exportGridToExcel(
    grid.value!.instance,
    'OperacionesAgua.xlsx',
    'OperacionesAgua',
    true
  );
};
</script>

<style lang="scss" scoped>


.lupa,
.chat,
.exportar-excel {
  color: var(--text-success-default);
}

.detalle {
  color: var(--text-primary-default);
  cursor: pointer;
}

.icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  font-size: 20px;
}

.grid-operaciones .dx-datagrid {
  max-height: calc(100vh - 250px);
}
</style>